import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LandingData } from "../core/buildLandingData";
import { useProfesional } from "../context/ProfesionalContext";
import { getEmpleosLanding } from "../api/landin";
import CardEmpleo from "../util/CardEmpleo";
import { BriefcaseBusiness } from "lucide-react";

interface Empleo {
  id?: string;
  titulo?: string;
  descripcion?: string;
  requisitos?: string;
  responsabilidades?: string;
  salario?: string;
  ubicacion?: string;
  modalidad?: string;
  imagen?: string;
}

interface Props {
  oferta?: Empleo;
  data?: LandingData;
}

const EmpleoLanding: React.FC<Props> = ({ oferta }) => {
  const { perfilId, landingData } = useProfesional();

  const styles = landingData?.theme?.globalStyles;

  const navigate = useNavigate();

  const [empleos, setEmpleos] = useState<Empleo[]>([]);
  const [indice, setIndice] = useState(0);

  const [mostrarControles, setMostrarControles] = useState(false);

  const mostrarTemporalmente = () => {
    setMostrarControles(true);

    clearTimeout((window as any).__empleosTimer);

    (window as any).__empleosTimer = setTimeout(() => {
      setMostrarControles(false);
    }, 2000);
  };

  useEffect(() => {
    if (perfilId === null) return;

    const cargarEmpleos = async () => {
      try {
        const res = await getEmpleosLanding(perfilId);

        if (res.ok) {
          setEmpleos(res.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    cargarEmpleos();
  }, [perfilId]);

  const siguiente = () => {
    setIndice((i) => (i + 1) % empleos.length);
    setMostrarControles(false);
  };

  const anterior = () => {
    setIndice((i) => (i - 1 + empleos.length) % empleos.length);
    setMostrarControles(false);
  };

  const empleo = oferta ?? empleos[indice];

  if (!empleo) {
    return (
      <div
        className="
        w-full
        max-w-4xl
        mx-auto
        rounded-3xl
        border border-white/10
        bg-white/5
        backdrop-blur-xl
        shadow-md shadow-zinc-800
        py-16 px-8
        flex flex-col items-center
        justify-center
        text-center
      "
      >
        <BriefcaseBusiness className="w-20 h-20 mb-6 text-cyan-400" />

        <h2
          className="text-3xl font-bold"
          style={{
            color: styles?.h2Color,
            fontFamily: styles?.fontFamily,
          }}
        >
          En estos momentos no hay ofertas de empleo
        </h2>

        <p
          className="mt-4 max-w-lg text-zinc-400"
          style={{
            color: styles?.textColor,
            fontFamily: styles?.fontFamily,
          }}
        >
          Actualmente no existen vacantes publicadas. Vuelve a visitarnos
          próximamente para conocer nuevas oportunidades laborales.
        </p>
      </div>
    );
  }

  return (
    <div
      className="
      group
      relative
      
      w-full
      max-w-4xl
      mx-auto
      
      h-auto
      lg:h-[420px]
      
      rounded-3xl
      
      border
      border-white/15
      
      bg-white/5
      
      shadow-md
      shadow-zinc-800
      
      transition-all
      duration-300
      hover:border-2
      hover:border-cyan-400/30
     
      overflow-visible
      lg:overflow-hidden
      
      "
      onMouseEnter={() => setMostrarControles(true)}
      onMouseLeave={() => setMostrarControles(false)}
      onMouseMove={mostrarTemporalmente}
      onTouchStart={mostrarTemporalmente}
    >
      {/* Contador */}
      {empleos.length > 1 && (
        <div
          className="
          absolute
          top-5
          right-8
          z-40
          px-3 py-1
          rounded-full
          bg-black/40
          backdrop-blur-md
          text-white text-sm font-semibold
        "
        >
          {indice + 1} / {empleos.length}
        </div>
      )}

      {/* Fondo 1 */}
      {empleos.length > 1 && (
        <div
          className="
          absolute inset-0
          translate-y-5
          rounded-3xl
          bg-white/10
          border border-white/10
          -z-10
        "
        />
      )}

      {/* Fondo 2 */}
      {empleos.length > 2 && (
        <div
          className="
          absolute inset-0
          translate-y-10
          rounded-3xl
          bg-white/5
          border border-white/10
          -z-20
        "
        />
      )}

      {/* Tarjeta */}
      <div className="relative w-full h-auto lg:h-full">
        <CardEmpleo
          empleo={empleo}
          styles={styles}
          className="w-full h-auto lg:h-full"
          onEnviar={() =>
            navigate("../contacto", {
              state: { empleo },
            })
          }
        />
      </div>

      {empleos.length > 1 && (
        <>
          <button
            onClick={anterior}
            className={`
            absolute
            left-5
            top-1/2
            -translate-y-1/2
            z-50
        
            w-12
            h-12
            rounded-full
        
            bg-cyan-500/20
            backdrop-blur-xl
            border
            border-cyan-400
        
            text-cyan-300
            text-3xl
        
            transition-all
            duration-300
        
            ${mostrarControles ? "opacity-100" : "opacity-0 pointer-events-none"}
        
            hover:bg-cyan-400
            hover:text-white
            hover:scale-110
            hover:shadow-[0_0_35px_#22d3ee]
          `}
          >
            ‹
          </button>

          <button
            onClick={siguiente}
            className={`
            absolute
            right-5
            top-1/2
            -translate-y-1/2
            z-50
            
            w-12
            h-12
            rounded-full
            
            bg-cyan-500/20
            backdrop-blur-xl
            border
            border-cyan-400
            
            text-cyan-300
            text-3xl
            
            transition-all
            duration-300
            
            ${mostrarControles ? "opacity-100" : "opacity-0 pointer-events-none"}
            
            hover:bg-cyan-400
            hover:text-white
            hover:scale-110
            hover:shadow-[0_0_35px_#22d3ee]
            `}
          >
            ›
          </button>
        </>
      )}
    </div>
  );
};

export default EmpleoLanding;
